import React, { useEffect, useState } from 'react';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Stack,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Close as CloseIcon,
  MoveUp as MoveIcon,
  Image as ImageIcon,
  Inventory as InventoryIcon,
  History as HistoryIcon,
  AutoFixHigh as AdjustTypeIcon,
  Map as MapIcon,
} from '@mui/icons-material';
import { ASSET_STATUS_KEYS } from '@equip/utils';
import { useAssetInventory, type AssetInventoryGroup } from '@/hooks/useAssetInventory';
import { useWriteGuard } from '@/hooks/useWriteGuard';
import { MoveAssetDialog } from './MoveAssetDialog';
import { AdjustAssetTypeDialog } from './AdjustAssetTypeDialog';
import { LoadingSkeleton } from '@/components/shared/LoadingSkeleton';
import { EntityFilterBar } from '@/components/shared/EntityFilterBar';
import { EntityActivityList } from '@/components/shared/EntityActivityList';
import { catalogImageService } from '@/services/catalogImageService';
import { useQuery } from '@tanstack/react-query';
import { httpClient } from '@/utils/httpClient';
import { API_CONFIG } from '@/config/api';
import { CatalogShelfMapViewer } from '@/components/shelf-map/CatalogShelfMapViewer';

interface AssetInventoryListProps {
  filters?: Record<string, string>;
}

export const AssetInventoryList: React.FC<AssetInventoryListProps> = ({ filters }) => {
  const isViewer = useWriteGuard();
  const { data, isLoading, error, refetch } = useAssetInventory(filters);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [adjustTypeDialogOpen, setAdjustTypeDialogOpen] = useState(false);
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [selectedMoveGroup, setSelectedMoveGroup] = useState<AssetInventoryGroup | null>(null);
  const [selectedAdjustGroup, setSelectedAdjustGroup] = useState<AssetInventoryGroup | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [previewCatalogId, setPreviewCatalogId] = useState<string | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [mapRow, setMapRow] = useState<AssetInventoryGroup | null>(null);

  // Resolve the root warehouse entity ID for the selected asset's leaf location.
  // The location_hierarchy view breadcrumb_ids[0] is always the root (warehouse).
  const { data: mapWarehouseId } = useQuery<string | null>({
    queryKey: ['asset-location-warehouse', mapRow?.location_id],
    queryFn: async () => {
      if (!mapRow?.location_id) return null;
      const result = await httpClient.get<Array<{ breadcrumb_ids: string[] }>>(
        `${API_CONFIG.ENDPOINTS.LOCATION_HIERARCHY}?id=eq.${mapRow.location_id}&select=breadcrumb_ids&limit=1`,
      );
      return result[0]?.breadcrumb_ids?.[0] ?? null;
    },
    enabled: !!mapRow?.location_id,
    staleTime: 60_000,
  });

  const handleMoveClick = (group: AssetInventoryGroup) => {
    setSelectedAssetIds(group.asset_ids);
    setSelectedMoveGroup(group);
    setMoveDialogOpen(true);
  };

  const handleAdjustTypeClick = (group: AssetInventoryGroup) => {
    setSelectedAdjustGroup(group);
    setAdjustTypeDialogOpen(true);
  };

  const handleMoveSuccess = () => {
    refetch();
  };

  const handleImageClick = (group: AssetInventoryGroup) => {
    if (!group.catalog_id || !group.has_catalog_image) return;
    setPreviewCatalogId(group.catalog_id);
  };

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    if (!previewCatalogId) {
      setPreviewImageUrl(null);
      return () => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
    }

    (async () => {
      try {
        const url = await catalogImageService.getImageObjectUrl(previewCatalogId);
        if (!cancelled) {
          objectUrl = url;
          setPreviewImageUrl(url);
        }
      } catch {
        if (!cancelled) {
          setPreviewImageUrl(null);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [previewCatalogId]);

  const handleExportCsv = () => {
    if (!data) return;
    const headers = ['Asset Name', 'LIN', 'NSN', 'Status', 'Location', 'Quantity'];
    const lines = [headers.join(',')];
    data.forEach((row) => {
      const rowValues = [
        `"${row.asset_name.replace(/"/g, '""')}"`,
        row.lin || '',
        row.nsn || '',
        row.status_key || '',
        `"${row.location_name.replace(/"/g, '""')} - ${row.location_path.replace(/"/g, '""')}"`,
        row.stock_quantity,
      ];
      lines.push(rowValues.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `asset-inventory-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns: GridColDef<AssetInventoryGroup>[] = [
    { field: 'asset_name', headerName: 'Asset Name', flex: 1, minWidth: 180 },
    { field: 'lin', headerName: 'LIN', width: 110 },
    { field: 'nsn', headerName: 'NSN', width: 140 },
    {
      field: 'status_key',
      headerName: 'Status',
      width: 110,
      valueFormatter: (value: string | null) => value || ASSET_STATUS_KEYS.AVAILABLE,
    },
    {
      field: 'location_name',
      headerName: 'Location',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Stack justifyContent="center" sx={{ height: '100%' }}>
          <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
            {params.value}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ lineHeight: 1.2, fontSize: '0.7rem' }}
          >
            {params.row.location_path}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'stock_quantity',
      headerName: 'Quantity',
      width: 100,
      type: 'number',
      align: 'right',
      headerAlign: 'left',
    },
    {
      field: 'image',
      headerName: 'Image',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row;
        if (!row.catalog_id) {
          return null;
        }
        const hasImage = Boolean(row.has_catalog_image);
        return (
          <IconButton
            size="small"
            disabled={!hasImage}
            onClick={() => handleImageClick(row)}
            aria-label={hasImage ? 'View catalog image' : 'No image available'}
            title={hasImage ? 'View catalog image' : 'No image available'}
          >
            <ImageIcon fontSize="small" color={hasImage ? 'primary' : 'disabled'} />
          </IconButton>
        );
      },
    },
    {
      field: 'map',
      headerName: 'Map',
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row;
        const hasLocation = !!row.location_id;
        return (
          <Tooltip title={hasLocation ? 'View on Shelf Map' : 'No location assigned'}>
            <span>
              <IconButton
                size="small"
                disabled={!hasLocation}
                onClick={() => setMapRow(row)}
                aria-label="View on Shelf Map"
              >
                <MapIcon fontSize="small" color={hasLocation ? 'primary' : 'disabled'} />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
    ...(!isViewer
      ? [
          {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams<AssetInventoryGroup>) => (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="center"
                sx={{ height: '100%' }}
              >
                <Tooltip
                  title={
                    !params.row.is_true_location &&
                    params.row.status_key !== ASSET_STATUS_KEYS.AVAILABLE
                      ? 'Move only allowed from valid physical locations'
                      : 'Move Assets'
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label="Move Assets"
                      onClick={() => handleMoveClick(params.row)}
                      disabled={
                        !params.row.is_true_location &&
                        params.row.status_key !== ASSET_STATUS_KEYS.AVAILABLE
                      }
                    >
                      <MoveIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    params.row.status_key !== ASSET_STATUS_KEYS.AVAILABLE
                      ? 'Adjust Type is only available for in-stock assets'
                      : !params.row.is_true_location
                        ? 'Adjust Type requires a physical location'
                        : 'Adjust Type'
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label="Adjust Type"
                      onClick={() => handleAdjustTypeClick(params.row)}
                      disabled={
                        params.row.status_key !== ASSET_STATUS_KEYS.AVAILABLE ||
                        !params.row.is_true_location ||
                        !params.row.location_id ||
                        !params.row.catalog_id ||
                        params.row.stock_quantity < 1
                      }
                    >
                      <AdjustTypeIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            ),
          } satisfies GridColDef<AssetInventoryGroup>,
        ]
      : []),
  ];

  if (isLoading) {
    return <LoadingSkeleton variant="grid" count={10} />;
  }

  if (error) {
    return (
      <Alert severity="error" action={<Button onClick={() => refetch()}>Retry</Button>}>
        Failed to load asset inventory.
      </Alert>
    );
  }

  return (
    <Stack spacing={2} sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <EntityFilterBar
        title="Asset Inventory"
        entityCount={data?.length || 0}
        onExport={!isViewer && activeTab === 0 ? handleExportCsv : undefined}
      />

      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2, flexShrink: 0 }}
        >
          <Tab icon={<InventoryIcon />} iconPosition="start" label="Inventory" />
          <Tab icon={<HistoryIcon />} iconPosition="start" label="Activity" />
        </Tabs>

        <CardContent
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            py: 1,
            '&:last-child': { pb: 1 },
          }}
        >
          {activeTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <DataGrid
                rows={data || []}
                columns={columns}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                disableRowSelectionOnClick
                getRowId={(row) => row.id}
                rowHeight={60}
              />
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <EntityActivityList entityType="asset" pollingInterval={5000} pageSize={25} />
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={Boolean(previewCatalogId)}
        onClose={() => setPreviewCatalogId(null)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: '90vw',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
          }}
        >
          Catalog Image
          <IconButton
            aria-label="Close"
            edge="end"
            onClick={() => setPreviewCatalogId(null)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {previewCatalogId && previewImageUrl && (
            <Box
              component="img"
              src={previewImageUrl}
              alt="Catalog image"
              sx={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <MoveAssetDialog
        open={moveDialogOpen}
        onClose={() => setMoveDialogOpen(false)}
        assetIds={selectedAssetIds}
        catalogId={selectedMoveGroup?.catalog_id}
        onSuccess={handleMoveSuccess}
      />

      <AdjustAssetTypeDialog
        open={adjustTypeDialogOpen}
        onClose={() => setAdjustTypeDialogOpen(false)}
        group={selectedAdjustGroup}
        onSuccess={handleMoveSuccess}
      />

      {mapRow && mapWarehouseId && (
        <CatalogShelfMapViewer
          locationContext={{
            itemName: mapRow.asset_name,
            primaryLocationId: mapRow.location_id ?? null,
            secondaryLocationId: null,
          }}
          warehouseEntityId={mapWarehouseId}
          onClose={() => setMapRow(null)}
          autoScrollToSelection
        />
      )}
    </Stack>
  );
};
